// @need-translate
import merge from 'lodash/merge';
import {RefObject} from 'react';
import {UrlLink} from '../../../applink/UrlLink';
import {tt} from '../../../common/getTranslations';
import {LogTabType} from '../../../common/LogTabType.enum';
import {useAdjustXAxisScrollPosition} from '../../../common/shared/hooks/useAdjustXAsixScrollPosition';
import {twMerge} from '../../../common/twMerge';
import {GnbMenuId} from '../../../domain/menu/constant';
import {getGnbMenuActionName} from '../../../domain/menu/util/getGnbMenuActionName';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {PageViewCustomPropsDataKey} from '../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Text} from '../../atoms/Text/Text';
import {ArrowDirection} from '../../molecules/Tooltip/ArrowDirection.enum';
import {Tooltip} from '../../molecules/Tooltip/Tooltip';
import {MobileMainTabItem} from '../MobileMainTabItem/MobileMainTabItem';
import {MainTabProps} from './MainTabProps.interface';

export function MobileMainTab({
  tabItems,
  currentTabItemId,
  onClickTab,
  showBorder = true,
  logTabType,
  isGnbTab,
  actionName,
  tabItemTiaraObject,
  className,
  ...props
}: Omit<MainTabProps, 'isMobile'>) {
  const {tabRef, onSelectTabItem} = useAdjustXAxisScrollPosition(
    tabItems?.findIndex(item => item.id === currentTabItemId)
  );

  if (!tabItems) {
    return null;
  }

  const isEqualArrangeTab = tabItems.length <= 4;

  return (
    <div
      className={twMerge(
        'relative flex h-44pxr w-full bg-theme-bg-base',
        className
      )}
      {...props}
    >
      {/**
       * 전체 width 크기를 가지고 MainTabItem의 하단 active border와 겹치지 않도록 하단 border를 보여주기 위해,
       * 부모 컨테이너와 동일 크기를 가진 별도의 absolute box를 추가로 활용합니다.
       */}
      {showBorder && (
        <div className="absolute top-0 left-0 h-full w-full border-b border-solid border-theme-transparent-20 bg-transparent" />
      )}

      <div
        ref={
          isEqualArrangeTab ? undefined : (tabRef as RefObject<HTMLDivElement>)
        }
        className={twMerge(
          'flex h-full w-full',
          !isEqualArrangeTab &&
            'space-x-24pxr overflow-x-scroll px-15pxr scrollbar-hide'
        )}
      >
        {tabItems.map((tabItem, index) => {
          const isSelected = currentTabItemId === tabItem.id;
          return (
            <div
              key={tabItem.text}
              className={twMerge(
                'relative flex items-center justify-center',
                isEqualArrangeTab ? 'flex-1' : 'shrink-0'
              )}
            >
              <UrlLink
                url={tabItem.href}
                // href가 있는 경우 a태그로 래핑되기 때문에 linkCss에 탭 아이템 스타일을 넣어줍니다.
                linkClassName={twMerge(
                  'z-10',
                  isEqualArrangeTab ? 'flex-1' : 'shrink-0'
                )}
                shouldOpenNewTab={tabItem.shouldOpenNewTab}
              >
                <MobileMainTabItem
                  // href가 없는 경우 PagewebLink 에서 a태그를 만들어주지 않아 PagewebLink에 부여된 탭 스타일이 적용되지 않으므로, 여기 직접 적용해줍니다.
                  className={
                    !tabItem.href
                      ? twMerge(
                          'z-10',
                          isEqualArrangeTab ? 'flex-1' : 'shrink-0'
                        )
                      : undefined
                  }
                  isActive={isSelected}
                  isEqualArrangeTab={isEqualArrangeTab}
                  hasDot={tabItem.hasDot}
                  {...putTiaraObjectProps(
                    merge(
                      {
                        click: {
                          layer1: isGnbTab ? '홈메뉴' : '서브탭',
                          copy: tabItem.text,
                          ordnum: String(index + 1),
                        },
                      },
                      tabItemTiaraObject
                    )
                  )}
                  onClick={e => {
                    LOG_CLICK_EVENT(
                      actionName
                        ? actionName
                        : isGnbTab
                          ? getGnbMenuActionName(tabItem.id as GnbMenuId)
                          : '서브탭_클릭',
                      e,
                      e => {
                        onSelectTabItem(e.target as HTMLElement);
                        onClickTab && onClickTab(tabItem.id);
                      }
                    );
                  }}
                  {...{
                    [PageViewCustomPropsDataKey.PageCategoryFilter]:
                      isSelected && logTabType === LogTabType.Category
                        ? `page_category_filter=${tabItem.text}`
                        : undefined,
                    [PageViewCustomPropsDataKey.PageGenreFilter]:
                      isSelected && logTabType === LogTabType.Genre
                        ? `page_genre_filter=${tabItem.text}`
                        : undefined,
                  }}
                >
                  {isSelected && <Text className="blind">선택됨, </Text>}
                  {tt(tabItem)}
                  <Text className="blind">
                    탭, {`총 ${tabItems.length}개 중 ${index + 1}번째`}
                  </Text>
                </MobileMainTabItem>
              </UrlLink>
              {/* 현재 메인탭 구현 상 스크롤이 생기는 시점부터는 툴팁을 띄울 수 없습니다(탭 전체의 overflow-x가 scroll로 되어있기 떄문). 요구사항이 아직은 없으니 추후 필요하면 그 때 고려해봐야 합니다. */}
              {isEqualArrangeTab &&
                tabItem.hasTooltip &&
                tabItem.tooltipMessage && (
                  <div className="absolute top-full">
                    <Tooltip
                      arrowDirection={ArrowDirection.Top}
                      text={tabItem.tooltipMessage}
                      showTooltip={true}
                      onClickTooltipCancel={tabItem.onClickCloseTooltip}
                    />
                  </div>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
